/* ==========================================================================
   pdp-variant-section-refresh.js

   Re-renders any section that opts in with `data-variant-refresh` whenever the
   shopper switches parent variant on a PDP, using Shopify's Section Rendering
   API (`?variant=<id>&section_id=<id>`). Server-side render means the section's
   Liquid re-resolves `product.selected_or_first_available_variant`, so any
   variant-scoped metafield it reads comes back correct with no client-side
   data duplication.

   WHY THIS EXISTS
   `global.js` renderProductInfo() is a *fragment* swap by DOM id (price-*,
   Sku-*, inventory) — it does not re-render arbitrary sections. So sections
   that read variant metafields need their own listener. This is the shared one.

   OPT IN
     <section id="shopify-section-{{ section.id }}" ...>   <- Shopify wraps this
       <div data-variant-refresh
            data-section-id="{{ section.id }}"
            data-product-url="{{ product.url }}"> ... </div>

   TWO INDEPENDENT TRIGGERS — deliberately belt-and-braces:

   (1) pub/sub `variant-change`. Published with THREE different shapes here;
       all three are normalised by variantIdFrom():
         global.js:1228            { data: { sectionId, html, variant } }
         pdp-hero.js:444           { source, variantId, sectionId }    <- no .data
         pdp-accessory-hero.js:269 { data: { variant: { id }, sectionId } }
       NOTE we reference the BARE global `PUB_SUB_EVENTS`, not
       `window.PUB_SUB_EVENTS`. constants.js declares it with `const` at the top
       level of a classic script, which lands in the global *declarative*
       environment and therefore is NOT a property of `window`. Every
       `if (window.PUB_SUB_EVENTS && ...)` guard in this theme is consequently
       dead — including the publish at pdp-hero.js:443, which is the hero the
       `pdp-bundle` template uses. That is a pre-existing theme bug, reported
       separately; this file does not depend on it being fixed, and will pick
       up the pub/sub path automatically if it ever is.

   (2) MutationObserver on `[data-pdp-hero]`'s `data-variant-id` attribute.
       pdp-hero.js sets that attribute on every successful variant swap
       (`hero.setAttribute('data-variant-id', newId)`), so this trigger works
       regardless of the pub/sub guard above. This is the path that actually
       fires on `pdp-bundle`.

   De-duplicated per section by last-rendered variant id, so both triggers
   firing for the same switch costs exactly one fetch.

   STATE PRESERVATION
   Elements carrying `data-refresh-key` have their <details open> state carried
   across the swap, so an open ingredient tab stays open when the edition
   changes.
   ========================================================================== */
(function () {
  'use strict';

  /* Two sections opt in on the pdp-bundle template (pdp-bundle-contents and
     pdp-bundle-tabs) and each emits its own <script src> for this file. The
     browser fetches it once but EXECUTES it once per tag, giving two
     independent IIFE states with two independent `lastVariant` de-dupe maps —
     so one edition switch fired 4 Section Rendering API requests for 2
     sections. The listeners are document-wide and refresh() already scans
     every [data-variant-refresh] holder, so one instance covers everything.
     Re-entry guard: second execution returns immediately. */
  if (window.__pdpVariantSectionRefreshLoaded) return;
  window.__pdpVariantSectionRefreshLoaded = true;

  var EVENT = 'variant-change';
  try {
    // Bare identifier on purpose — see header note. typeof guard keeps this
    // safe if constants.js ever fails to load.
    if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      EVENT = PUB_SUB_EVENTS.variantChange;
    }
  } catch (e) { /* keep the literal fallback */ }

  var inFlight = {};
  var lastVariant = {};

  function variantIdFrom(event) {
    if (!event) return null;
    var d = event.data ? event.data : event;
    var id = d.variantId || (d.variant && d.variant.id) || null;
    if (!id && d.variant && d.variant.id === 0) id = 0;
    return id == null ? null : String(id);
  }

  function captureOpenState(root) {
    var state = {};
    root.querySelectorAll('[data-refresh-key]').forEach(function (el) {
      state[el.getAttribute('data-refresh-key')] = el.hasAttribute('open') || el.classList.contains('is-open');
    });
    return state;
  }

  function restoreOpenState(root, state) {
    root.querySelectorAll('[data-refresh-key]').forEach(function (el) {
      var k = el.getAttribute('data-refresh-key');
      if (!(k in state)) return;
      if (el.tagName === 'DETAILS') {
        if (state[k]) el.setAttribute('open', '');
        else el.removeAttribute('open');
      }
    });
  }

  function refresh(holder, variantId) {
    var sectionId = holder.getAttribute('data-section-id');
    var productUrl = holder.getAttribute('data-product-url') || window.location.pathname;
    if (!sectionId) return;

    var wrapper = document.getElementById('shopify-section-' + sectionId);
    if (!wrapper) return;

    // De-dupe: both triggers can fire for one shopper action.
    if (lastVariant[sectionId] === String(variantId)) return;
    lastVariant[sectionId] = String(variantId);

    var key = sectionId;
    if (inFlight[key]) inFlight[key].abort();
    var ctl = new AbortController();
    inFlight[key] = ctl;

    var url = productUrl.split('?')[0] +
      '?variant=' + encodeURIComponent(variantId) +
      '&section_id=' + encodeURIComponent(sectionId);

    wrapper.setAttribute('data-variant-refresh-state', 'loading');

    fetch(url, { signal: ctl.signal })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status + ' for ' + url);
        return res.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.getElementById('shopify-section-' + sectionId);
        if (!fresh) throw new Error('Section ' + sectionId + ' absent from Section Rendering API response');

        var open = captureOpenState(wrapper);
        wrapper.innerHTML = fresh.innerHTML;
        restoreOpenState(wrapper, open);

        var newHolder = wrapper.querySelector('[data-variant-refresh]');
        var state = newHolder ? (newHolder.getAttribute('data-render-state') || 'unknown') : 'missing-holder';
        wrapper.setAttribute('data-variant-refresh-state', state);

        // Loud about the silent-empty case: a panel that renders nothing and
        // says nothing is indistinguishable from a broken one.
        if (state === 'empty') {
          console.warn('[pdp-variant-section-refresh] Section "' + sectionId +
            '" rendered EMPTY for variant ' + variantId +
            ' — the variant/product metafield it reads has no value.');
        }

        document.dispatchEvent(new CustomEvent('pdp:section-refreshed', {
          bubbles: true,
          detail: { sectionId: sectionId, variantId: variantId, state: state }
        }));
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        delete lastVariant[sectionId];
        wrapper.setAttribute('data-variant-refresh-state', 'error');
        console.error('[pdp-variant-section-refresh] ' + sectionId + ' refresh failed', err);
      })
      .finally(function () {
        if (inFlight[key] === ctl) delete inFlight[key];
      });
  }

  function onVariantChange(event) {
    var variantId = variantIdFrom(event);
    if (!variantId) {
      console.warn('[pdp-variant-section-refresh] variantChange fired with no resolvable variant id', event);
      return;
    }
    document.querySelectorAll('[data-variant-refresh]').forEach(function (holder) {
      refresh(holder, variantId);
    });
  }

  /* ---- Trigger 1: pub/sub ---- */
  function bindPubSub() {
    if (typeof window.subscribe === 'function') {
      window.subscribe(EVENT, onVariantChange);
      return true;
    }
    return false;
  }

  /* ---- Trigger 2: hero data-variant-id MutationObserver ---- */
  var heroObserver = null;
  function bindHeroObserver() {
    var heroes = document.querySelectorAll('[data-pdp-hero][data-variant-id]');
    if (!heroes.length) return false;

    heroObserver = new MutationObserver(function (records) {
      records.forEach(function (rec) {
        if (rec.attributeName !== 'data-variant-id') return;
        var id = rec.target.getAttribute('data-variant-id');
        if (!id) return;
        document.querySelectorAll('[data-variant-refresh]').forEach(function (holder) {
          refresh(holder, id);
        });
      });
    });

    heroes.forEach(function (hero) {
      // Seed the de-dupe with the server-rendered variant so the first real
      // switch is not swallowed and a no-op re-render never fires.
      document.querySelectorAll('[data-variant-refresh]').forEach(function (holder) {
        var sid = holder.getAttribute('data-section-id');
        if (sid && lastVariant[sid] === undefined) {
          lastVariant[sid] = String(hero.getAttribute('data-variant-id'));
        }
      });
      heroObserver.observe(hero, { attributes: true, attributeFilter: ['data-variant-id'] });
    });
    return true;
  }

  function bind() {
    var ps = bindPubSub();
    var ho = bindHeroObserver();
    if (!ps || !ho) {
      document.addEventListener('DOMContentLoaded', function () {
        if (!ps) ps = bindPubSub();
        if (!ho) ho = bindHeroObserver();
        if (!ps && !ho) {
          console.warn('[pdp-variant-section-refresh] No variant trigger available — ' +
            'variant-aware sections will not update on this page.');
        }
      });
    }
  }

  bind();
  window.PDPVariantSectionRefresh = {
    refresh: refresh,
    variantIdFrom: variantIdFrom,
    _state: function () { return { event: EVENT, last: lastVariant }; }
  };
})();
